/* eslint-disable camelcase */
import {
  AppWindow,
  AudioLines,
  CalendarDays,
  CalendarX,
  CircleMinus,
  CircleQuestionMark,
  Cpu,
  GitBranch,
  Globe,
  ListOrdered,
  Mail,
  Mic,
  Monitor,
  Network,
  Server,
  Smartphone,
  Tag,
  Timer,
  User,
  UserPlus,
  Users,
  Video,
} from 'lucide-react'

import styles from './DimensionIcon.module.scss'

import type { AttributionDimension } from '@/api/attributionObservability'
import appleLogo from '@/assets/icons/apple.svg'
import googleLogo from '@/assets/icons/google.svg'
import microsoftLogo from '@/assets/icons/microsoft.svg'
import googleMeetLogo from '@/assets/meetingPlatforms/google-meet.svg'
import slackLogo from '@/assets/meetingPlatforms/slack.svg'
import teamsLogo from '@/assets/meetingPlatforms/teams.svg'
import windowsLogo from '@/assets/meetingPlatforms/windows.svg'
import zoomLogo from '@/assets/meetingPlatforms/zoom.svg'

type Dimension =
  AttributionDimension | 'participant_source' | 'meetings' | 'label_source' | 'channel_rule' | 'llm_route'

const platformLogos: Record<string, string> = {
  google_meet: googleMeetLogo,
  slack: slackLogo,
  teams: teamsLogo,
  zoom: zoomLogo,
}

const dimensionIcons = {
  platform: Video,
  os: Monitor,
  client: Globe,
  calendar_event: CalendarDays,
  participant_bucket: Users,
  user_meeting_bucket: ListOrdered,
  participant_source: Users,
  ax_bucket: AudioLines,
  recording_length_bucket: Timer,
  backend_version: Server,
  app_version: Tag,
  meetings: Video,
  label_source: Users,
  channel_rule: AudioLines,
  llm_route: GitBranch,
}

const valueIcon = (dimension: Dimension, value?: string) => {
  if (dimension === 'label_source') {
    if (value === 'ax') return Network
    if (value === 'channel') return AudioLines
    if (value === 'llm') return Cpu
    if (value === 'unnamed') return CircleQuestionMark
  }
  if (value === 'unknown' || value === 'other' || value === 'mixed') return CircleQuestionMark
  if (value === 'not_applicable') return CircleMinus
  if (dimension === 'os' && value === 'ios') return Smartphone
  if (dimension === 'platform' && value === 'in_person') return Users
  if (dimension === 'calendar_event' && value === 'none') return CalendarX
  if (dimension === 'participant_bucket' && value === '1') return User
  if (dimension === 'user_meeting_bucket' && value === '1') return UserPlus
  if (dimension === 'client' && value === 'native') return AppWindow
  if (dimension === 'participant_source' && value === 'canonical') return Mic
  if (dimension === 'participant_source' && value === 'invited') return Mail
  return dimensionIcons[dimension]
}

const DimensionIcon = ({ dimension, value, size = 16 }: { dimension: Dimension; value?: string; size?: number }) => {
  const logo =
    dimension === 'platform'
      ? platformLogos[value ?? '']
      : dimension === 'os' && value === 'macos'
        ? appleLogo
        : dimension === 'os' && value === 'windows'
          ? windowsLogo
          : dimension === 'calendar_event' && value === 'google'
            ? googleLogo
            : dimension === 'calendar_event' && value === 'outlook'
              ? microsoftLogo
              : undefined
  const Icon = valueIcon(dimension, value)
  return (
    <span className={styles.icon} style={{ width: size, height: size }} aria-hidden="true">
      {logo ? (
        <img className={styles.logo} src={logo} width={size} height={size} alt="" draggable={false} />
      ) : (
        <Icon size={size} strokeWidth={1.7} focusable="false" aria-hidden="true" />
      )}
    </span>
  )
}

export default DimensionIcon
